import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, debounceTime, distinctUntilChanged, switchMap, catchError, of, map, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

interface AccountFormData {
  email: string;
  password: string;
  confirmPassword?: string;
  agreedToTerms?: boolean;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

interface CompanyFormData {
  companyName: string;
  country: string;
  vatNumber?: string;
  address?: CompanyAddress;
}
import {
  CheckEmailResponse,
  CheckCompanyNameResponse,
  SelfRegisterRequest,
  RegistrationSuccess,
  RegistrationState,
  RegistrationStep,
  EmailValidationState,
  CompanyAddress,
  CreateAccountData
} from '../models/registration.models';

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  private http = inject(HttpClient);
  
  // API endpoints
  private readonly apiUrl = environment.apiUrl;
  private readonly checkEmailUrl = `${this.apiUrl}/auth/check-email`;
  private readonly checkCompanyNameUrl = `${this.apiUrl}/auth/check-company-name`;
  private readonly selfRegisterUrl = `${this.apiUrl}/auth/self-register`;
  private readonly requestAccessUrl = `${this.apiUrl}/auth/request-access`;
  private readonly verifyEmailUrl = `${this.apiUrl}/auth/verify-email`;
  private readonly resendVerificationUrl = `${this.apiUrl}/auth/resend-verification`;

  // State management
  private registrationState$ = new BehaviorSubject<RegistrationState>({
    currentStep: RegistrationStep.ACCOUNT,
    isLoading: false
  });

  // Email validation subject for real-time validation
  private emailValidation$ = new BehaviorSubject<EmailValidationState>({});

  constructor() {}

  // State getters
  getRegistrationState(): Observable<RegistrationState> {
    return this.registrationState$.asObservable();
  }

  getEmailValidationState(): Observable<EmailValidationState> {
    return this.emailValidation$.asObservable();
  }

  getCurrentStep(): RegistrationStep {
    return this.registrationState$.value.currentStep;
  }

  getCurrentRegistrationState(): RegistrationState {
    return this.registrationState$.value;
  }

  // API calls
  checkEmail(email: string): Observable<CheckEmailResponse> {
    return this.http.post<{success: boolean, data: CheckEmailResponse}>(this.checkEmailUrl, { email })
      .pipe(
        map(response => response.data),
        catchError((error) => {
          console.error('Error checking email:', error);
          // Return a safe default response instead of throwing
          return of({
            emailType: 'invalid',
            emailAlreadyExists: false,
            companyExists: false,
            domain: '',
            companyName: undefined,
            companyId: undefined,
          } as CheckEmailResponse);
        })
      );
  }

  checkCompanyName(companyName: string): Observable<CheckCompanyNameResponse> {
    return this.http.post<{success: boolean, data: CheckCompanyNameResponse}>(this.checkCompanyNameUrl, { companyName })
      .pipe(
        map(response => response.data),
        catchError((error) => {
          console.error('Error checking company name:', error);
          // Return a safe default response - assume name is available if check fails
          return of({
            isAvailable: true,
            matches: [],
            suggestions: undefined,
          } as CheckCompanyNameResponse);
        })
      );
  }

  selfRegister(data: SelfRegisterRequest): Observable<RegistrationSuccess> {
    return this.http.post<RegistrationSuccess>(this.selfRegisterUrl, data)
      .pipe(
        catchError((error) => {
          console.error('Self registration failed:', error);
          // Re-throw the error with meaningful message for the component to handle
          const errorMessage = error.error?.message || 'Registration failed. Please try again.';
          throw { ...error, userFriendlyMessage: errorMessage };
        })
      );
  }

  requestAccess(data: {
    email: string;
    firstName: string;
    lastName: string;
    companyId: string;
    message?: string;
    preferredLanguage?: string;
  }): Observable<RegistrationSuccess> {
    return this.http.post<RegistrationSuccess>(this.requestAccessUrl, data)
      .pipe(
        catchError((error) => {
          console.error('Access request failed:', error);
          // Re-throw the error with meaningful message for the component to handle
          const errorMessage = error.error?.message || 'Failed to send access request. Please try again.';
          throw { ...error, userFriendlyMessage: errorMessage };
        })
      );
  }

  // Email validation with debounce for real-time checking
  validateEmailRealTime(email: string): Observable<EmailValidationState> {
    if (!email || email.length < 3) {
      return of({ isValid: false, message: 'Email required' });
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return of({ isValid: false, message: 'Invalid email format' });
    }

    // Set loading state
    this.emailValidation$.next({ isChecking: true });

    return of(email).pipe(
      debounceTime(500), // Wait 500ms after user stops typing
      distinctUntilChanged(),
      switchMap((emailValue) => 
        this.checkEmail(emailValue).pipe(
          switchMap((result) => {
            // Rejeter les emails déjà utilisés
            if (result.emailAlreadyExists) {
              const validationState: EmailValidationState = {
                isValid: false,
                emailType: result.emailType,
                isChecking: false,
                message: 'This email address is already registered'
              };
              this.emailValidation$.next(validationState);
              
              // Update registration state with check result (add the checked email)
              const resultWithEmail = { ...result, checkedEmail: emailValue };
              this.updateRegistrationState({
                emailCheckResult: resultWithEmail
              });
              
              return of(validationState);
            }

            // Accepter tous les emails valides qui ne sont pas déjà utilisés
            const isValid = result.emailType !== 'invalid';
            const validationState: EmailValidationState = {
              isValid: isValid,
              emailType: result.emailType,
              isChecking: false,
              message: result.emailType === 'invalid' ? 'Invalid email format' : undefined
            };
            
            this.emailValidation$.next(validationState);
            
            // Update registration state with check result (add the checked email)
            const resultWithEmail = { ...result, checkedEmail: emailValue };
            this.updateRegistrationState({
              emailCheckResult: resultWithEmail
            });
            
            return of(validationState);
          }),
          catchError((error) => {
            const errorState: EmailValidationState = {
              isValid: false,
              isChecking: false,
              message: error.error?.message || 'Email validation failed'
            };
            this.emailValidation$.next(errorState);
            return of(errorState);
          })
        )
      )
    );
  }

  // State management methods
  updateRegistrationState(updates: Partial<RegistrationState>): void {
    const currentState = this.registrationState$.value;
    const newState = { ...currentState, ...updates };
    this.registrationState$.next(newState);
  }

  setCurrentStep(step: RegistrationStep): void {
    this.updateRegistrationState({ currentStep: step });
  }

  setLoading(loading: boolean): void {
    this.updateRegistrationState({ isLoading: loading });
  }

  setError(error: string | undefined): void {
    this.updateRegistrationState({ error, isLoading: false });
  }

  // Navigation methods
  goToCompanyStep(accountData: AccountFormData): void {
    this.updateRegistrationState({
      currentStep: RegistrationStep.COMPANY,
      accountData: accountData as CreateAccountData
    });
  }

  goBackToAccountStep(): void {
    this.updateRegistrationState({
      currentStep: RegistrationStep.ACCOUNT
    });
  }

  // Reset state
  resetRegistration(): void {
    this.registrationState$.next({
      currentStep: RegistrationStep.ACCOUNT,
      isLoading: false
    });
    this.emailValidation$.next({});
  }

  // Build final registration request
  buildSelfRegisterRequest(accountData: AccountFormData, companyData: CompanyFormData): SelfRegisterRequest {
    return {
      email: accountData.email,
      password: accountData.password,
      firstName: accountData.firstName || 'User', // Default for now
      lastName: accountData.lastName || 'Name', // Default for now
      phone: accountData.phone,
      companyName: companyData.companyName,
      companyDetails: {
        vatNumber: companyData.vatNumber,
        address: companyData.address
      }
    };
  }

  // Email verification methods
  verifyEmail(token: string): Observable<{success: boolean, message: string}> {
    return this.http.post<{success: boolean, message: string}>(this.verifyEmailUrl, { token })
      .pipe(
        catchError((error) => {
          console.error('Error verifying email:', error);
          return throwError(() => ({
            success: false,
            message: error.error?.message || 'Email verification failed. Please try again.'
          }));
        })
      );
  }

  resendVerification(email: string): Observable<{success: boolean, message: string}> {
    return this.http.post<{success: boolean, message: string}>(this.resendVerificationUrl, { email })
      .pipe(
        catchError((error) => {
          console.error('Error resending verification:', error);
          return throwError(() => ({
            success: false,
            message: error.error?.message || 'Failed to resend verification email. Please try again.'
          }));
        })
      );
  }

  // Store email for verification flow
  storePendingVerificationEmail(email: string): void {
    localStorage.setItem('pendingVerificationEmail', email);
  }

  getPendingVerificationEmail(): string | null {
    return localStorage.getItem('pendingVerificationEmail');
  }

  clearPendingVerificationEmail(): void {
    localStorage.removeItem('pendingVerificationEmail');
  }

  // Utility methods
  isEmailProfessional(email: string): boolean {
    const freeEmailDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    return !!domain && !freeEmailDomains.includes(domain);
  }

  parseInviteEmails(inviteText: string): string[] {
    if (!inviteText?.trim()) return [];
    
    return inviteText
      .split(/[,\n\r]+/) // Split by comma, newline, or carriage return
      .map(email => email.trim())
      .filter(email => email.length > 0)
      .filter(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)); // Basic email validation
  }
}